/**
 * ProfileBuilder — the master-profile entry point for the Resume Workspace.
 *
 * Two states, one component:
 *   - No profile yet → Tabs[Upload | Paste]. Upload takes .pdf/.docx/.txt via
 *     multipart (drag-drop or browse); Paste takes raw resume text (≥100 chars).
 *     Both run through React Query mutations and call props.onChanged() on success
 *     so the parent can refetch the profile + usage.
 *   - Profile exists → a summary card: identity headline, domain-readiness bars,
 *     strengths / red flags, and the AI narrative. A "Rebuild" button drops the
 *     user back into the tabs to replace the profile.
 *
 * `structured_profile` is an untyped Record from the backend (BuildProfileOutput),
 * so every field is read defensively with optional chaining + array fallbacks.
 */

import React, { useCallback, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  ResumeProfile,
  buildResumeProfile,
  uploadResumeProfile,
} from "../../api/client";
import LessonMarkdown from "../LessonMarkdown";

// coral via theme.palette.primary — per-product accent deferred (spec D2)
const ACCEPTED_EXTS = [".pdf", ".docx", ".txt"];
const MIN_PASTE_CHARS = 100;

interface ProfileBuilderProps {
  profile: ResumeProfile | null;
  onChanged: () => void;
}

/** Pull a friendly message off our structured 422/429/415 error envelopes. */
function errorMessage(err: any, t: TFunction): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return detail?.message || t("ProfileBuilder.buildError");
}

export default function ProfileBuilder({ profile, onChanged }: ProfileBuilderProps) {
  // When a profile exists the user can opt back into the tabs to replace it.
  const [rebuilding, setRebuilding] = useState(false);

  if (profile && !rebuilding) {
    return <ProfileSummary profile={profile} onRebuild={() => setRebuilding(true)} />;
  }

  return (
    <ProfileInput
      onChanged={() => {
        setRebuilding(false);
        onChanged();
      }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Input state — Upload / Paste tabs                                          */
/* -------------------------------------------------------------------------- */

function ProfileInput({ onChanged }: { onChanged: () => void }) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const [tab, setTab] = useState<"upload" | "paste">("upload");
  const [pasteText, setPasteText] = useState("");
  const [pasteTouched, setPasteTouched] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadResumeProfile(file),
    onSuccess: onChanged,
  });

  const buildMutation = useMutation({
    mutationFn: (text: string) => buildResumeProfile(text),
    onSuccess: onChanged,
  });

  const pending = uploadMutation.isPending || buildMutation.isPending;
  const serverError = uploadMutation.error || buildMutation.error;

  const handleFile = useCallback(
    (file: File) => {
      setClientError(null);
      uploadMutation.reset();
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
      if (!ACCEPTED_EXTS.includes(ext)) {
        setClientError(t("ProfileBuilder.invalidFileType"));
        return;
      }
      uploadMutation.mutate(file);
    },
    [uploadMutation, t]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [handleFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (pending) return;
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile, pending]
  );

  const pasteTooShort = pasteText.trim().length < MIN_PASTE_CHARS;
  const showPasteError = pasteTouched && pasteTooShort && pasteText.length > 0;

  const handleBuild = () => {
    setPasteTouched(true);
    if (pasteTooShort) return;
    buildMutation.reset();
    buildMutation.mutate(pasteText.trim());
  };

  return (
    <Card
      variant="outlined"
      sx={{ borderColor: alpha(theme.palette.primary.light, 0.25), borderRadius: 3, overflow: "hidden" }}
    >
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5 }}>
        <Typography
          variant="overline"
          sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.1em" }}
        >
          {t("ProfileBuilder.stepLabel")}
        </Typography>
        <Typography variant="h6" fontWeight={500} sx={{ mt: 0.25 }}>
          {t("ProfileBuilder.heading")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
          {t("ProfileBuilder.subheading")}
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          px: { xs: 2, sm: 3 },
          mt: 1.5,
          minHeight: 44,
          "& .MuiTab-root": { minHeight: 44, fontWeight: 500, textTransform: "none" },
          "& .Mui-selected": { color: `${theme.palette.primary.main} !important` },
          "& .MuiTabs-indicator": { bgcolor: theme.palette.primary.main },
        }}
      >
        <Tab value="upload" label={t("ProfileBuilder.uploadTab")} icon={<UploadFileIcon fontSize="small" />} iconPosition="start" />
        <Tab value="paste" label={t("ProfileBuilder.pasteTab")} icon={<AutoAwesomeIcon fontSize="small" />} iconPosition="start" />
      </Tabs>

      <Divider />

      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {tab === "upload" ? (
          <Stack spacing={1.5}>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_EXTS.join(",")}
              style={{ display: "none" }}
              onChange={handleFileSelect}
              disabled={pending}
            />
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                if (!pending) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !pending && fileInputRef.current?.click()}
              sx={{
                border: 2,
                borderStyle: "dashed",
                borderColor: dragOver ? theme.palette.primary.light : alpha(theme.palette.primary.light, 0.3),
                borderRadius: 2.5,
                py: 5,
                px: 3,
                textAlign: "center",
                cursor: pending ? "default" : "pointer",
                bgcolor: dragOver ? alpha(theme.palette.primary.light, 0.06) : alpha(theme.palette.primary.light, 0.02),
                transition: "all 0.18s ease",
                "&:hover": pending
                  ? {}
                  : { borderColor: theme.palette.primary.light, bgcolor: alpha(theme.palette.primary.light, 0.05) },
              }}
            >
              {pending ? (
                <Stack spacing={1.5} alignItems="center">
                  <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2" color="text.secondary">
                    {t("ProfileBuilder.analyzing")}
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={1} alignItems="center">
                  <UploadFileIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t("ProfileBuilder.dragDropHint")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("ProfileBuilder.acceptedFormats")}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              multiline
              minRows={8}
              maxRows={18}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              onBlur={() => setPasteTouched(true)}
              disabled={pending}
              placeholder={t("ProfileBuilder.pastePlaceholder")}
              error={showPasteError}
              helperText={
                showPasteError
                  ? t("ProfileBuilder.pasteTooShort", { min: MIN_PASTE_CHARS })
                  : t("ProfileBuilder.charCount", { count: pasteText.trim().length })
              }
            />
            <Box sx={{ alignSelf: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleBuild}
                disabled={pending || pasteTooShort}
                startIcon={
                  pending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <AutoAwesomeIcon />
                  )
                }
                sx={{
                  fontWeight: 500,
                  textTransform: "none",
                  px: 3,
                }}
              >
                {pending ? t("ProfileBuilder.analyzing") : t("ProfileBuilder.buildProfileButton")}
              </Button>
            </Box>
          </Stack>
        )}

        {clientError && (
          <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setClientError(null)}>
            {clientError}
          </Alert>
        )}
        {serverError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage(serverError, t)}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Summary state — structured profile view                                    */
/* -------------------------------------------------------------------------- */

interface DomainReadiness {
  domain?: string;
  label?: string;
  score?: number;
  rationale?: string;
}

function ProfileSummary({
  profile,
  onRebuild,
}: {
  profile: ResumeProfile;
  onRebuild: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const sp = profile.structured_profile ?? {};
  const identity: string = sp.identity ?? t("ProfileBuilder.defaultIdentity");
  const domains: DomainReadiness[] = sp.domain_readiness ?? [];
  const strengths: string[] = sp.top_strengths ?? [];
  const redFlags: string[] = sp.red_flags ?? [];

  return (
    <Card
      variant="outlined"
      sx={{ borderColor: alpha(theme.palette.primary.light, 0.25), borderRadius: 3 }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        {/* Header: identity + rebuild */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: "0.1em" }}
            >
              {t("ProfileBuilder.masterProfileLabel")}
            </Typography>
            <Typography variant="h5" fontWeight={500} sx={{ mt: 0.25, lineHeight: 1.25 }}>
              {identity}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRebuild}
            sx={{
              flexShrink: 0,
              color: theme.palette.primary.main,
              borderColor: alpha(theme.palette.primary.light, 0.4),
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { borderColor: theme.palette.primary.light, bgcolor: alpha(theme.palette.primary.light, 0.05) },
            }}
          >
            {t("ProfileBuilder.rebuild")}
          </Button>
        </Stack>

        {/* Domain readiness bars */}
        {domains.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1.5 }}>
              {t("ProfileBuilder.domainReadiness")}
            </Typography>
            <Stack spacing={1.75}>
              {domains.map((d, i) => {
                const value = Math.max(0, Math.min(100, d?.score ?? 0));
                return (
                  <Box key={`${d?.domain ?? "domain"}-${i}`}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {[d?.domain, d?.label].filter(Boolean).join(" ") || t("ProfileBuilder.domainFallback")}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, color: theme.palette.primary.main, fontVariantNumeric: "tabular-nums" }}
                      >
                        {value}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={value}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.primary.light, 0.12),
                        "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: theme.palette.primary.light },
                      }}
                    />
                    {d?.rationale && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                        {d.rationale}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Strengths + red flags, side by side on wide screens */}
        {(strengths.length > 0 || redFlags.length > 0) && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mt: 3 }}>
            {strengths.length > 0 && (
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "success.main" }} />
                  <Typography variant="subtitle2" fontWeight={500}>
                    {t("ProfileBuilder.topStrengths")}
                  </Typography>
                </Stack>
                <Box component="ul" sx={{ pl: 2.5, m: 0, "& li": { mb: 0.5 } }}>
                  {strengths.map((s, i) => (
                    <Box component="li" key={i}>
                      <Typography variant="body2">{s}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {redFlags.length > 0 && (
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                  <WarningAmberIcon sx={{ fontSize: 18, color: "warning.main" }} />
                  <Typography variant="subtitle2" fontWeight={500}>
                    {t("ProfileBuilder.watchOuts")}
                  </Typography>
                </Stack>
                <Box component="ul" sx={{ pl: 2.5, m: 0, "& li": { mb: 0.5 } }}>
                  {redFlags.map((s, i) => (
                    <Box component="li" key={i}>
                      <Typography variant="body2">{s}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        )}

        {/* AI narrative */}
        {profile.narrative_markdown && (
          <>
            <Divider sx={{ my: 3 }} />
            <LessonMarkdown>{profile.narrative_markdown ?? ""}</LessonMarkdown>
          </>
        )}
      </CardContent>
    </Card>
  );
}
